import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@reverbdotcom/commons/src/gql';
import React from 'react';
import { WithRouterProps } from 'react-router';
import track from 'react-tracking';
import classNames from 'classnames';
import Item2BuyingGuide from './item2_buying_guide';
import { Item2Description } from './item2_description';
import { Item2ProductSpecs } from './item2_product_specs';
import Item2Footer from './item2_footer';
import { Item2Title } from './item2_title';
import { Item2Breadcrumbs } from './item2_breadcrumbs';
import { Item2Video } from './item2_video';
import Item2PaidLanding from './item2_paid_landing';
import Item2EndedListingRow, { unqualifiedToRenderEndedListingRow } from './item2_ended_listing_row';
import Item2Pricing from './item2_pricing';
import Item2ShippingWarning from './item2_shipping_warning';
import { Item2Image } from './item2_image';
import { Item2FlagListing } from './item2_flag_listing';
import Item2OrderDetails from './item2_order_details';
import ErrorDisplay from './discovery/error_display';
import { CoreListingLayout } from '@reverbdotcom/commons/src/gql/graphql';
import { useItemTrackView } from './use_item_track_view';
import { useViewTracking } from '@reverbdotcom/commons/src//use_tracking';
import Item2Metadata from './item2_metadata';
import useProductViewAction from './use_product_view_action';
import AppInstallCallout from './app_install_callout';
import Item2ShippingPromptModal from './item2_shipping_prompt_modal';
import { WATCH_PROMPT_PARAM } from './shared/constants';
import SellYoursCTA from './sell_yours_cta';
import { Paths, withWebHost } from './shared/url_helpers';
import {
  Item2WhyReverbSignals,
  Item2NudgeSignals,
} from './item2_nudge_signals';
import { ListingPaymentOptions } from './ListingPaymentOptions';
import { Item2AdWithCspsRowContainer } from './item2_ad_with_csps_row_container';
import { Item2ShopModule } from './item2_shop_module';
import { Item2ProductModule } from './item2_product_module';
import { Item2StickySidebar } from './item2_sticky_sidebar';
import { Item2ShopOverview } from './item2_shop_overview';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { experimentInt, isOwner } from '@reverbdotcom/commons/src/user_context_helpers';
import ErrorBoundary from '@reverbdotcom/commons/src/components/error_boundary';
import { US_CON, LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import { userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { localizeSimilarListingsFilters } from '@reverbdotcom/commons/src/localize_listing_rows_utils';
import {
  availableOfferButtonAction,
} from '@reverbdotcom/commons/src/offers/create_offer_button';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import Item2ImageWatchBadge, { watchBadgeNextBestAction } from './item2_image_watch_badge';
import { ProductReviewsLayout } from './ProductReviewsLayout';
import experiments from '@reverbdotcom/commons/src/experiments';
import { RefreshedItem2Layout } from './refreshed_item2_layout';

export type IProps = ChildProps<WithRouterProps & IUserContext, CoreListingLayout.Query, Location>;

const COMPONENT_NAME = 'Item2Layout';

export function hasUserBoughtItem({ data }: IProps): boolean {
  const { listing } = data;
  return listing?.orders && listing.orders.nodes.length > 0;
}

export function fallback() {
  return (
    <div className="height-vh-50 d-flex fx-align-center fx-justify-center">
      <ErrorDisplay />
    </div>
  );
}

export function showShippingPrompt({ user, listing, hasPublishConfirmation }) {
  const usConShippingRegion = user.shippingRegionCode === US_CON;
  const listingIsLive = listing.state === LISTING_STATES.LIVE;
  const isCarrierCalculated = !!listing.shipmentPackage;

  return listingIsLive && !isCarrierCalculated && hasPublishConfirmation && usConShippingRegion && (isOwner(user, listing) || user.isAdmin);
}

export function Item2Layout(props: IProps) {
  const { user, data } = props;
  const listingId = props?.data?.listing?.id;
  const location = props?.location;
  const queryParams = location?.query || {};
  const isQualifiedToRenderAdWithCspsRow = !unqualifiedToRenderEndedListingRow(
    props.data?.listing,
    hasUserBoughtItem(props),
    isOwner(user, props.data?.listing),
  );

  useItemTrackView(listingId, user);
  useProductViewAction(props);

  const offerButtonActionPresent = Boolean(availableOfferButtonAction(props.data?.me?.availableActions));

  useViewTracking(
    {
      eventName: MParticleEventName.ViewedOfferCallout,
      entityId: availableOfferButtonAction(props.data?.me?.availableActions)?.id,
      listingId: listingId,
      signals: props.data?.listing?.signals?.map(signal => signal.name).join('|'),
    },
    offerButtonActionPresent,
  );

  useViewTracking(
    {
      eventName: MParticleEventName.ViewedWatchCallout,
      entityId: !offerButtonActionPresent ? watchBadgeNextBestAction(props.data?.me?.availableActions)?.id : undefined,
      listingId: listingId,
    },
    !offerButtonActionPresent && Boolean(watchBadgeNextBestAction(props.data?.me?.availableActions)),
  );

  if (!data || data.loading || !data.listing) return null;

  // eslint-disable-next-line prefer-object-spread
  const flagListingUserProps = Object.assign({}, props.user, props.data.me);

  const { listing } = data;
  const hasPublishConfirmation = Boolean(queryParams.publish_confirmation);
  const displayShippingPrompt = showShippingPrompt({ user, listing, hasPublishConfirmation });
  const productContentClasses = classNames(
    'item2__content',
    'item2__content--product',
    { 'item2__content--hidden': !listing.csp },
  );

  const expGroup = experimentInt(user, experiments.WEB_ITEM_PAGE_RESTRUCTURE);

  if (expGroup > 0) {
    return (
      <RefreshedItem2Layout {...props} />
    );
  }

  return (
    <ErrorBoundary
      fallback={fallback()}
    >
      <AppInstallCallout
        calloutLocation="item"
        canonicalUrl={withWebHost(Paths.item.expand({ id: listingId, bk: queryParams?.bk }))}
      />

      <Item2ShippingPromptModal
        listing={listing}
        showModal={displayShippingPrompt}
      />

      <div className="item2">
        <Item2Metadata
          listing={listing}
        />
        <div className="dynamic-page dynamic-page--listing">
          {
            hasUserBoughtItem(props) &&
            <Item2OrderDetails
              orders={listing.orders}
            />
          }
          <div className="dynamic-page__content">
            <Item2PaidLanding
              listing={listing}
              location={location}
            />
            <Item2EndedListingRow
              listing={listing}
              similarListings={listing?.similarListings}
              userIsBuyer={hasUserBoughtItem(props)}
              userIsSeller={isOwner(user, listing)}
            />
            <Item2Breadcrumbs
              listing={listing}
            />
            <div className="item2__content">
              <div className="item2__content__section item2__content__section--image">
                <div className="item2__image">
                  <Item2Image
                    listing={listing}
                  />
                  <div className="item2__watch-badge">
                    <Item2ImageWatchBadge
                      listing={listing}
                      me={props.data?.me}
                      availableActions={props.data?.me?.availableActions}
                    />
                  </div>
                </div>
              </div>
              <div className="item2__content__sidebar">
                <Item2StickySidebar>
                  <Item2Title
                    listing={listing}
                  />
                  {listing.state === LISTING_STATES.LIVE &&
                    <Item2ShippingWarning
                      listing={listing}
                    />
                  }
                  <Item2Pricing
                    listing={listing}
                    negotiations={props.data.me?.myNegotiations?.negotiations || []}
                    cartItems={props.data.me?.cart?.cartItems || []}
                    userTotalOffersCount={props.data.me?.profile?.totalBuyerOffersCreated}
                    watchPrompt={!!queryParams[WATCH_PROMPT_PARAM]}
                    availableActions={props.data?.me?.availableActions}
                    me={props.data?.me}
                    showKlarnaCallout
                  />
                  <Item2ShopOverview
                    listing={listing}
                  />
                  {listing.state === LISTING_STATES.LIVE &&
                    <Item2NudgeSignals
                      listing={listing}
                    />
                  }
                  <ListingPaymentOptions listing={listing} />
                </Item2StickySidebar>
              </div>
              <div className="item2__content__section">
                <Item2Description
                  listing={listing}
                />
              </div>
              <div className="item2__content__section">
                <Item2ProductSpecs
                  listing={listing}
                />
              </div>
              <div className="item2__content__section item2__content__section--buying-guide">
                <Item2BuyingGuide
                  listing={listing}
                />
              </div>
              {listing.state === LISTING_STATES.LIVE &&
                <Item2WhyReverbSignals
                  listing={listing}
                />
              }
              <div className="item2__content__section">
                <Item2ShopModule
                  listing={listing}
                  location={location}
                />
              </div>
            </div>
            <div className={productContentClasses}>
              <div className="item2__content__section">
                <div>
                  <Item2ProductModule
                    listing={listing}
                  />
                </div>
                <ProductReviewsLayout
                  cspId={listing.csp?.id}
                  trackingDisplayStyle="product"
                  location={location}
                />
              </div>
              <div className="item2__content__sidebar">
                <Item2Video
                  listing={listing}
                />
                <SellYoursCTA csp={listing.csp} />
              </div>
            </div>
            <div className="item2__content">
              <div className="item2__content__section">
                <Item2FlagListing
                  user={flagListingUserProps}
                  listing={listing}
                  id="item2_flag_listing"
                />
              </div>
            </div>
          </div>
        </div>
        {
          isQualifiedToRenderAdWithCspsRow &&
          <div className="scaling-mb-6">
            <Item2AdWithCspsRowContainer
              listing={listing}
            />
          </div>
        }
        <Item2Footer listing={listing} />
      </div>
    </ErrorBoundary>
  );
}

const withQuery = withGraphql<IProps, CoreListingLayout.Query>(
  gql(`
    query Core_Listing_Layout(
      $id: String
      $loggedOut: Boolean!
      $shouldSkipTracking: Boolean!
      $itemRegion: String,
      $itemRegionRelation: reverb_search_ListingItemRegionRelation
      $useNewBreadcrumbs: Boolean!
    ) {
      listing(
        input: {id: $id}
      ) {
        _id
        id
        ...ShippingWarningData
        ...Item2PricingData
        ...Item2DescriptionData
        ...Item2BuyingGuideData
        ...Item2TitleData
        ...Item2BreadcrumbsData @include(if: $useNewBreadcrumbs)
        ...Item2FallbackBreadcrumbsData @skip(if: $useNewBreadcrumbs)
        ...Item2Image
        ...Item2Video
        ...Item2FlagListingData
        ...Item2MetadataData
        ...Item2OrderDetailsData
        ...Item2ProductViewTrackingData @skip(if: $shouldSkipTracking)
        ...Item2EndedListingData
        ...Item2PaidLandingData
        ...WatchBadgeData
        ...SignalsData
        ...Item2ProductSpecsData
        ...ItemFooterFields
        ...Item2AdWithCspsRowContainerData
        ...Item2ShopModuleData
        ...Item2AboutThisSellerData
        ...Item2ShopOverviewData
        ...Item2ProductModuleData
        ...Item2AboutThisListingData
        ...Item2StickyBannerData
        ...Item2ProductModuleRefreshedData

        similarListings(input: {
          itemRegion: $itemRegion,
          itemRegionRelation: $itemRegionRelation,
          limit: 10,
        }) {
          _id
          ...SimilarListingsFields
        }
        csp {
          _id
          featuredListing {
            listing {
              _id
              price {
                display
              }
            }
          }
          slug
        }
        shop {
          _id
          name
          ...FavoriteShopCTAData
        }
        pricing {
          buyerPrice {
            amount
            currency
          }
        }
        shipmentPackage {
          _id
          width {
            value
            unit
          }
          height {
            value
            unit
          }
          length {
            value
            unit
          }
          weight {
            value
            unit
          }
        }
        experiments {
          name
          bucket
        }
        affirmFinancingPromotion {
          zeroPercentFinancing
        }
      }
      me @skip(if: $loggedOut) {
        ...Item2PriceWithShippingCartItemData
        ...Item2LoggedInOffersCountData
        ...CreateUserActionMeData
        myNegotiations (
          input: {
            as: BUYER,
            limit: 1,
            listingIds: [ $id ]
            sort: "updated_at|desc"
          }
        ) {
          negotiations {
            _id
            ...Item2PricingNegotiationsData
          }
        }
        availableActions(input: { listingId: $id }) {
          ...Item2AvailableActionsData
        }
        ...Item2FlagListingMeData
      }
    }
  `),
  {
    options: (ownProps) => {
      return {
        errorPolicy: 'all',
        ssr: true,
        variables: {
          id: ownProps.params.id.split('-')[0],
          loggedOut: !!ownProps.user.loggedOut,
          shouldSkipTracking: !userContextIsTrackable(ownProps.user),
          ...localizeSimilarListingsFilters(ownProps.user),
          useNewBreadcrumbs: ownProps.user?.locale === 'en', // new breadcrumbs use browse pages which currently don't have translations
        },
      };
    },
  },
);

export default track({ componentName: COMPONENT_NAME })(withUserContext(withQuery(Item2Layout)));
